import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, MapPin, Route, CheckCircle2, ChevronRight, Wind, Layers, Cog } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { CONTACT, SINCE, breadcrumb, faqSchema, SITE } from "./marketingData";
import { cityBySlug, cityFaqs, CITIES, verticalByKey } from "./seoContent";

const ICONS = { Wind, Layers, Cog };

export default function CityPage({ verticalKey }) {
  const { city } = useParams();
  const v = verticalByKey(verticalKey);
  const c = cityBySlug(city);
  useEffect(() => { window.scrollTo(0, 0); }, [city]);
  if (!v) return <Navigate to="/" replace />;
  if (!c) return <Navigate to={v.base} replace />;

  const Icon = ICONS[v.icon] || Wind;
  const faqs = cityFaqs(c, v);
  const path = `${v.base}/${c.slug}`;
  const title = `${v.noun} in ${c.name}`;
  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Service",
      name: `${v.vehicle} transport in ${c.name}`,
      serviceType: `${v.vehicle} transport`,
      areaServed: { "@type": "City", name: c.name },
      provider: {
        "@type": "MovingCompany", name: "Indian Transport Organisation",
        telephone: "+91-8492894602", url: `${SITE}/`,
        address: { "@type": "PostalAddress", streetAddress: "313, Mohid Heights, RTO Lane, Andheri West", addressLocality: "Mumbai", addressRegion: "MH", postalCode: "400053", addressCountry: "IN" },
      },
      description: `${v.vehicle} transport in ${c.name}, ${c.state} for fragile, precision and over-dimensional cargo, to any pincode in India since ${SINCE}.`,
    },
    breadcrumb([
      { name: "Home", path: "/" },
      { name: v.noun, path: v.base },
      { name: c.name, path },
    ]),
    faqSchema(faqs),
  ];

  const others = CITIES.filter((x) => x.slug !== c.slug).slice(0, 6);

  return (
    <div className="bg-[#F9F8F6] text-[#1A1A18] font-body antialiased overflow-x-hidden" data-testid={`city-page-${v.key}-${c.slug}`}>
      <Seo
        title={`${title} | ITO (since ${SINCE})`}
        description={`ITO provides ${v.vehicle} transport in ${c.name}, ${c.state} — covering ${c.areas}. Cargo moved safely to any pincode. Get a quote.`}
        path={path}
        image={v.image}
        imageAlt={`${v.noun} in ${c.name}`}
        keywords={`${v.noun.toLowerCase()} in ${c.name.toLowerCase()}, ${v.vehicle} transport ${c.name.toLowerCase()}, ${c.name.toLowerCase()} machinery transporter, ${c.name.toLowerCase()} odc transporter`}
        jsonLd={jsonLd}
      />
      <MarketingNav heroDark />

      {/* Hero */}
      <section className="relative min-h-[54vh] flex items-end pb-14 pt-32 overflow-hidden">
        <img src={v.image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A18]/93 via-[#1A1A18]/58 to-[#1A1A18]/28" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 w-full">
          <div className="flex items-center gap-2 text-xs text-white/80 mb-5">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={v.base} className="hover:text-white">{v.noun}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-[#D4A373]">{c.name}</span>
          </div>
          <span className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-[#C84B31] mb-5"><Icon className="w-6 h-6 text-white" /></span>
          <h1 className="font-heading font-black tracking-tight text-white text-3xl sm:text-5xl leading-[1.05] max-w-3xl">{title}</h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl leading-relaxed">
            {v.lead} across {c.name}, {c.state} — to any pincode in India, since {SINCE}.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-xs font-semibold tracking-widest uppercase text-[#C84B31] mb-3">Overview</p>
              <p className="text-base text-[#4F4C44] leading-relaxed">
                {c.note ? c.note + " " : ""}Indian Transport Organisation provides {v.vehicle} transport in and around {c.name},
                serving {c.areas}. {v.descriptor}
              </p>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="font-heading font-bold text-2xl mb-5">What we move from {c.name}</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {v.cargo.map((x) => (
                  <li key={x} className="flex items-center gap-3 text-[#1A1A18]">
                    <CheckCircle2 className="w-5 h-5 text-[#C84B31] shrink-0" /> {x}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal className="mt-10">
              <h2 className="font-heading font-bold text-2xl mb-5">Popular routes from {c.name}</h2>
              <div className="flex flex-wrap gap-2.5" data-testid="city-routes">
                {c.routes.map((r) => (
                  <span key={r} className="inline-flex items-center gap-2 rounded-full border border-[#1A1A18]/12 bg-white px-3.5 py-1.5 text-sm text-[#1A1A18]">
                    <Route className="w-3.5 h-3.5 text-[#C84B31]" /> {r}
                  </span>
                ))}
                <span className="inline-flex items-center gap-2 rounded-full border border-[#1A1A18]/12 bg-white px-3.5 py-1.5 text-sm text-[#1A1A18]">
                  <MapPin className="w-3.5 h-3.5 text-[#C84B31]" /> {c.name} → any pincode in India
                </span>
              </div>
              <p className="mt-5 text-sm text-[#4F4C44] leading-relaxed">
                Over-dimensional loads from {c.name} are handled end to end — route surveys, state permits, RTO clearances and pilot escorts —
                with live GPS tracking and a dedicated coordinator on every movement.
              </p>
            </Reveal>
          </div>

          {/* CTA card */}
          <Reveal delay={0.1} className="lg:col-span-1">
            <div className="rounded-2xl bg-[#1A1A18] text-white p-8 sticky top-24" data-testid="city-cta-card">
              <h2 className="font-heading font-bold text-xl mb-2">Book from {c.name}</h2>
              <p className="text-white/75 text-sm leading-relaxed mb-6">Share your cargo, route and dates — we'll quote your {v.vehicle} movement.</p>
              <a href={CONTACT.phoneHref} data-testid="city-quote-btn" className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
                <Phone className="w-4 h-4" /> Call {CONTACT.phone}
              </a>
              <a href={CONTACT.emailHref} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:bg-white hover:text-[#1A1A18] transition-colors">
                Email us
              </a>
              <Link to={`/guides/${v.guideSlug}`} className="mt-4 block text-center text-sm font-semibold text-[#D4A373] hover:text-white transition-colors">
                Read the {v.vehicle} guide →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection faqs={faqs} title={`${v.vehicle} transport in ${c.name} — FAQ`} testid="city-faq" />

      {/* Other cities */}
      <section className="py-14 bg-white border-t border-[#1A1A18]/10">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <h2 className="font-heading font-bold text-2xl mb-6">{v.noun} in other cities</h2>
          <div className="flex flex-wrap gap-2.5">
            {others.map((o) => (
              <Link key={o.slug} to={`${v.base}/${o.slug}`} data-testid={`city-other-${o.slug}`}
                className="rounded-full border border-[#1A1A18]/15 px-3.5 py-1.5 text-sm text-[#1A1A18] hover:border-[#C84B31] hover:text-[#C84B31] transition-colors">
                {o.name}
              </Link>
            ))}
          </div>
          <Link to={v.base} className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C84B31]">
            <ArrowLeft className="w-4 h-4" /> All cities
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
